import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin } from 'rxjs';
import { SelectionService } from '../../../core/services/selection.service';
import { CaseContextService } from '../../../core/services/case-context.service';
import { CaseApiService } from '../../../core/services/case-api.service';
import { CompareSbsService } from '../../../shared/comparison/services/compare-sbs.service';
import { CompareNavigationService } from '../../../core/services/compare-navigation.service';
import { TOPBAR_POPOVER_CLOSE } from '../topbar-popover.service';

@Component({
  selector: 'app-basket-dropdown',
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Header -->
    <div class="bd-head">
      <span class="bd-title">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
        </svg>
        Basket
      </span>
      <div class="bd-pips" [attr.aria-label]="products().length + ' of ' + maxCount + ' products selected'">
        @for (i of pipIndices; track i) {
          <span class="bd-pip" [class.filled]="i < products().length"></span>
        }
      </div>
      <!-- ROR-3486: the shared .bn-x-btn close (same glyph, size and hover as the
           row removes below and the comparison page's ✕). -->
      <button class="bn-x-btn" type="button" (click)="close()" aria-label="Close basket">
        <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
        </svg>
      </button>
    </div>

    <!-- Product List -->
    <div class="bd-list">
      @if (products().length === 0) {
        <div class="bd-empty">
          <svg class="bd-empty-ico" width="34" height="34" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
          <p class="bd-empty-title">Your basket is empty</p>
          <p class="bd-empty-sub">Add up to 4 products to compare side-by-side</p>
        </div>
      } @else {
        @for (p of products(); track p.selectionId) {
          <div class="bd-item">
            <div class="bd-badge" [style.background]="badgeColor(p.carrier)">{{ initials(p.carrier) }}</div>
            <div class="bd-body">
              <div class="bd-name">{{ p.name }}</div>
              <div class="bd-sub">{{ p.carrier }}<span class="bd-sep"> · </span>{{ p.productType.toUpperCase() }}</div>
            </div>
            <button class="bd-remove bn-x-btn" type="button" (click)="onRemoveProduct(p.selectionId)"
                    [attr.aria-label]="'Remove ' + p.name">
              <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        }
      }
    </div>

    <!-- Save to Case -->
    @if (caseLoaded() && products().length) {
      <div class="bd-case" [class.bd-case--saved]="saveState() === 'saved'">
        <div class="bd-case-icon">
          @if (saveState() === 'saved') {
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
              <polyline points="1.5 5.5 4.5 8.5 9.5 2.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          } @else {
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
            </svg>
          }
        </div>
        <span class="bd-case-text">
          @if (saveState() === 'saved') {
            Added to <b>{{ caseName() }}</b>
          } @else {
            Shortlist to <b>{{ caseName() }}</b>?
          }
        </span>
        <button class="bd-case-btn" type="button"
                [disabled]="saveState() === 'saving' || saveState() === 'saved'"
                (click)="onSaveToCase()">
          {{ saveState() === 'saving' ? 'Saving…' : saveState() === 'saved' ? 'Added' : 'Add to case' }}
        </button>
      </div>
    }

    <!-- Footer -->
    <div class="bd-foot">
      <span class="bd-foot-note">Products only — benefits in Calculator</span>
      <!-- Hidden while a comparison is already on screen: offering "Compare 3"
           there either re-navigates to the page you are reading or, worse,
           looks like it would apply the basket's current contents. -->
      @if (!compareNav.isComparing()) {
        <button class="bd-compare" type="button"
                [disabled]="!canCompare()" [title]="compareTip()" (click)="onCompare()">
          Compare
          @if (products().length > 0) {
            <span class="bd-compare-n">{{ products().length }}</span>
          }
        </button>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 360px;
      background: #fff;
      border-radius: 12px;
      border: 1px solid #D5DDE8;
      box-shadow: 0 4px 6px rgba(13,42,69,0.04), 0 12px 40px rgba(13,42,69,0.13);
      overflow: hidden;
      font-family: inherit;
    }

    /* ── Header ── */
    .bd-head {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 11px 14px;
      background: linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%);
      border-bottom: 1px solid #E2E8F0;
    }
    .bd-title {
      display: flex;
      align-items: center;
      gap: 5px;
      font-size: 12px;
      font-weight: 700;
      color: var(--navy);
      flex-shrink: 0;
      svg { opacity: 0.65; }
    }
    .bd-pips {
      display: flex;
      align-items: center;
      gap: 3px;
      flex: 1;
      padding-left: 4px;
    }
    .bd-pip {
      width: 16px;
      height: 4px;
      border-radius: 2px;
      background: #D5DDE8;
      transition: background 0.2s;
      &.filled { background: var(--amber, #9A7B2C); }
    }

    /* ── Product List ── */
    .bd-list {
      padding: 4px 0;
      max-height: 300px;
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: #CBD5E1 transparent;
      &::-webkit-scrollbar { width: 4px; }
      &::-webkit-scrollbar-track { background: transparent; }
      &::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 2px; }
    }

    /* Empty State */
    .bd-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 22px 16px 24px;
      gap: 3px;
    }
    .bd-empty-ico {
      color: #C8D3DF;
      margin-bottom: 8px;
    }
    .bd-empty-title {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--navy);
      margin: 0;
    }
    .bd-empty-sub {
      font-size: 11px;
      color: var(--muted);
      margin: 0;
      text-align: center;
      max-width: 210px;
      line-height: 1.5;
    }

    /* Product Item */
    .bd-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 7px 12px 7px 14px;
      transition: background 0.1s;
      &:hover {
        background: #F8FAFC;
        .bd-remove { opacity: 1; }
      }
    }
    .bd-badge {
      width: 32px;
      height: 32px;
      border-radius: 6px;
      color: #fff;
      font-family: 'Roboto Mono', ui-monospace, monospace;
      font-size: 10px;
      font-weight: 700;
      display: grid;
      place-items: center;
      flex-shrink: 0;
      letter-spacing: 0.04em;
      box-shadow: inset 0 1px 0 rgba(255,255,255,0.15), 0 1px 3px rgba(0,0,0,0.15);
    }
    .bd-body { flex: 1; min-width: 0; }
    .bd-name {
      font-size: 12px;
      font-weight: 600;
      color: var(--navy);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .bd-sub {
      font-size: 10.5px;
      color: var(--muted);
      margin-top: 1px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .bd-sep { opacity: 0.45; }
    /* Size, shape, colour and hover all come from the shared .bn-x-btn in
       styles.scss. The only thing particular to the popover is that the
       button stays hidden until its row is hovered. */
    .bd-remove {
      opacity: 0;
      /* Hover reveals it for the mouse; without this, tabbing to it moved an
         invisible focus ring down the list. */
      &:focus-visible { opacity: 1; }
    }

    /* ── Save to Case ── */
    .bd-case {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 14px;
      background: rgba(154,123,44,0.06);
      border-top: 1px solid rgba(154,123,44,0.18);
      border-bottom: 1px solid rgba(154,123,44,0.18);
    }
    .bd-case-icon {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: rgba(154,123,44,0.14);
      color: var(--amber, #9A7B2C);
      display: grid;
      place-items: center;
      flex-shrink: 0;
    }
    .bd-case--saved .bd-case-icon {
      background: rgba(34,139,98,0.14);
      color: #228B62;
    }
    .bd-case-text {
      flex: 1;
      font-size: 11px;
      color: var(--text2);
      b { color: var(--navy); }
    }
    .bd-case-btn {
      min-width: 72px;
      height: 26px;
      padding: 0 11px;
      background: var(--amber, #9A7B2C);
      color: #fff;
      border: none;
      border-radius: 5px;
      font-family: inherit;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;
      white-space: nowrap;
      transition: opacity 0.12s, background 0.12s;
      &:disabled { opacity: 0.55; cursor: default; }
    }
    .bd-case--saved .bd-case-btn { background: #228B62; }

    /* ── Footer ── */
    .bd-foot {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      padding: 9px 14px;
      background: linear-gradient(180deg, #F1F5F9 0%, #F8FAFC 100%);
      border-top: 1px solid #E2E8F0;
    }
    .bd-foot-note {
      font-size: 10px;
      color: var(--muted);
      font-style: italic;
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .bd-compare {
      display: flex;
      align-items: center;
      gap: 5px;
      height: 28px;
      padding: 0 13px;
      background: var(--navy);
      color: #fff;
      border: none;
      border-radius: 5px;
      font-family: inherit;
      font-size: 11.5px;
      font-weight: 600;
      cursor: pointer;
      flex-shrink: 0;
      transition: background 0.12s, opacity 0.12s;
      &:hover:not(:disabled) { background: var(--blue); }
      &:disabled { opacity: 0.4; cursor: not-allowed; }
    }
    .bd-compare-n {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background: rgba(255,255,255,0.22);
      font-size: 10px;
      font-weight: 700;
      font-family: 'Roboto Mono', ui-monospace, monospace;
    }
  `],
})
export class BasketDropdownComponent {
  private readonly closeFn = inject(TOPBAR_POPOVER_CLOSE);
  private readonly selection = inject(SelectionService);
  private readonly caseCtx = inject(CaseContextService);
  private readonly caseApi = inject(CaseApiService);
  private readonly compareSbs = inject(CompareSbsService);
  // protected, not private: the template reads isComparing() to hide Compare.
  protected readonly compareNav = inject(CompareNavigationService);

  protected readonly maxCount = 4;
  protected readonly pipIndices = [0, 1, 2, 3];
  protected readonly products = this.selection.products;
  protected readonly caseLoaded = computed(() => !!this.caseCtx.activeCase());
  protected readonly caseName = computed(() => this.caseCtx.activeCase()?.name ?? '');
  protected readonly saveState = signal<'idle' | 'saving' | 'saved'>('idle');

  protected onSaveToCase(): void {
    const c = this.caseCtx.activeCase();
    const items = this.products();
    if (!c || items.length === 0) return;
    this.saveState.set('saving');
    forkJoin(items.map(p => this.caseApi.addCandidate(c.id, {
      productId: p.id,
      productType: p.productType,
      name: p.name,
      carrier: p.carrier,
      source: 'basket',
    }))).subscribe({
      next: () => this.saveState.set('saved'),
      error: () => this.saveState.set('idle'),
    });
  }

  protected close(): void { this.closeFn(); }
  protected onRemoveProduct(selectionId: string): void { this.selection.removeProduct(selectionId); }

  /**
   * A comparison needs two products. The button was only disabled at zero, so
   * with one basketed product it looked live and onCompare() then returned
   * without navigating anywhere.
   */
  protected readonly canCompare = computed(() => this.products().length >= 2);

  protected readonly compareTip = computed(() => {
    const n = this.products().length;
    if (n >= 2) return `Compare ${n} products side-by-side`;
    return n === 1
      ? 'Add one more product to compare'
      : 'Add at least 2 products to compare';
  });

  protected onCompare(): void {
    const items = this.products();
    if (items.length < 2) return;
    const riders = items.slice(0, 4).map(p => ({
      id: p.id,
      productid: p.id,
      companyName: p.carrier,
      productName: p.name,
      productType: p.productType.toUpperCase() as 'FA' | 'IA' | 'RILA' | 'VA',
      rateRow: p.rateRow,
      // Present only for products ticked on a rates grid — the Interest tab
      // shows those picks and leaves every other entry point as it was.
      rateRows: this.selection.rateGridRows(p),
    }));
    const staged = this.compareSbs.stageContext({ riders, scenario: null });
    if (!staged) return;
    this.closeFn();
    this.compareNav.routeToCompare();
  }

  protected initials(carrier: string): string {
    const parts = carrier.trim().split(/\s+/);
    if (parts.length === 0) return '—';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }

  protected badgeColor(carrier: string): string {
    const palette = ['#1B4E7C', '#2D7D6F', '#5C4A8F', '#7C3D2D', '#2D5A3D', '#6B3E7C', '#3D5A7C', '#7C5A2D'];
    let h = 0;
    for (let i = 0; i < carrier.length; i++) h = (Math.imul(31, h) + carrier.charCodeAt(i)) | 0;
    return palette[Math.abs(h) % palette.length];
  }
}
